import type { ColumnDef } from "@tanstack/react-table"

import TestTemplateProductsCell from "../components/TestTemplatesTable/TestTemplateProductsCell"
import TestTemplateRowActions from "../components/TestTemplatesTable/TestTemplateRowActions"
import { getTestTemplateComparisonLabel, getTestTemplateResultUomLabel } from "../utils"

import DateChip from "@/components/DateChip"
import { PermissionActionType } from "@/constants/permissions"
import type { TestTemplate } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"

export const useTestTemplateColumns = (onRefresh: () => void): ColumnDef<TestTemplate>[] => {
  const { hasPermissions } = usePermissionManager()

  return [
    {
      accessorKey: "externalSystemId",
      header: "Test Number",
      enableSorting: false,
      size: 170,
      cell: ({ row }) => row.original.externalSystemId,
    },
    {
      accessorKey: "description",
      header: "Description",
      enableSorting: false,
      size: 300,
      cell: ({ row }) => row.original.description,
    },
    {
      accessorKey: "associatedTestTemplatesCount",
      header: "Associated Products",
      enableSorting: false,
      size: 200,
      cell: ({ row }) => <TestTemplateProductsCell testTemplate={row.original} />,
    },
    {
      accessorKey: "resultsUnitOfMeasure",
      header: "Results UoM",
      enableSorting: false,
      cell: ({ row }) => getTestTemplateResultUomLabel(row.original.resultsUnitOfMeasure),
    },
    {
      accessorKey: "comparison",
      header: "Comparison",
      enableSorting: false,
      cell: ({ row }) => getTestTemplateComparisonLabel(row.original.comparison),
    },
    {
      accessorKey: "createdOn",
      header: "Created",
      enableSorting: true,
      sortingFn: "alphanumeric",
      size: 120,
      cell: ({ row }) => <DateChip date={row.original.createdOn} />,
    },
    {
      accessorKey: "updatedOn",
      header: "Updated",
      enableSorting: true,
      sortingFn: "alphanumeric",
      size: 120,
      cell: ({ row }) => <DateChip date={row.original.updatedOn} />,
    },
    ...(hasPermissions([PermissionActionType.TEST_TEMPLATE_MAINTENANCE])
      ? [
          {
            accessorKey: "actions",
            header: "",
            enableSorting: false,
            size: 72,
            cell: ({ row }) => <TestTemplateRowActions row={row} onRefresh={onRefresh} />,
          } as ColumnDef<TestTemplate>,
        ]
      : []),
  ]
}
